import {
  Button,
  Empty,
  Modal,
  Segmented,
  Tabs,
  Tooltip,
  Typography,
} from "antd";
import {
  CloseOutlined,
  ColumnWidthOutlined,
  FileTextOutlined,
  FormOutlined,
} from "@ant-design/icons";
import { FC, useEffect, useState } from "react";
import { IFetchAllDetails } from "../../../../interfaces/IPatient";
import ResubmissionProcedureCghsNew from "./ResubmissionProcedureCghsNew";
import ReasonOfResubmissionCghsNew from "./ReasonOfResubmissionCghsNew";
import { Document } from "../../PreAuth/PreAuthIhx/PreAuthIhx";
import "./ResubmissionCompareModalCghsNew.css";

const { Text } = Typography;

type LayoutMode = "split" | "procedures" | "bill";

type HospitalBillFile = {
  fileUrl: string;
  fileName: string;
  id?: number;
};

type ResubmissionCompareModalCghsNewProps = {
  addNewResubmissionModal: boolean;
  handleCancel: () => void;
  closeCreateResubmissionModal: any;
  handleSubmit: any;
  isFetchingPortalData: boolean;
  resubmissionProcedure: any;
  formattedAccreditationOptions: Array<{ label: string; value: string }>;
  formattedTreatingDoctorsOptions: Array<{ label: string; value: string }>;
  formattedProcedureReferral: Array<{ label: string; value: string }>;
  formattedProceduresOptions: Array<{ label: string; value: string }>;
  handleGetProcedureAmount: CallableFunction;
  handleGetProcedureStrats: CallableFunction;
  patientDetails: IFetchAllDetails | null;
  procedureStrats: any;
  handleDeleteProcedure: CallableFunction;
  setIsModalOpen: CallableFunction;
  isModalOpen: boolean;
  handleOk: CallableFunction;
  form: any;
  resubmissionReasonOptions: Array<{ label: string; value: string }>;
  reasonOfResubmissionForm?: any;
  documentTypes: Document[];
  documentUrls: {
    documentType: string;
    documentName: string;
    documentUrl: string;
    id: number;
    referenceType?: string | null;
    documentLabelType: any;
    deficiencyCheckStatus: any;
    remarks?: string | null;
  }[];
  handleFileChange: any;
  handleOpen: any;
  isFetchingBillDetails: any;
  isSubmitting: any;
  procedureForm: any;
  hospitalBills?: HospitalBillFile[];
};

const ResubmissionCompareModalCghsNew: FC<
  ResubmissionCompareModalCghsNewProps
> = (props) => {
  const hospitalBills = props?.hospitalBills || [];
  const hasHospitalBills = hospitalBills.length > 0;
  const [layoutMode, setLayoutMode] = useState<LayoutMode>("split");
  const [selectedBillIdx, setSelectedBillIdx] = useState(0);

  useEffect(() => {
    if (props?.addNewResubmissionModal) {
      setLayoutMode(hasHospitalBills ? "split" : "procedures");
      setSelectedBillIdx(0);
    }
  }, [props?.addNewResubmissionModal, hasHospitalBills]);

  useEffect(() => {
    if (selectedBillIdx >= hospitalBills.length) {
      setSelectedBillIdx(0);
    }
  }, [hospitalBills, selectedBillIdx]);

  const selectedBill =
    hospitalBills.length > 0 ? hospitalBills[selectedBillIdx] : null;

  const showProcedures =
    layoutMode === "procedures" ||
    layoutMode === "split" ||
    !hasHospitalBills;
  const showBill =
    hasHospitalBills && (layoutMode === "bill" || layoutMode === "split");

  const isSplit = showProcedures && showBill && layoutMode === "split";

  return (
    <Modal
      open={props?.addNewResubmissionModal}
      centered={false}
      width="140vw"
      style={{
        top: 0,
        paddingBottom: 0,
        maxWidth: "140vw",
        transformOrigin: "top left",
        margin: "20px 20px 20px 40px",
      }}
      wrapClassName="resubmission-compare-modal-wrap"
      className="resubmission-compare-modal"
      title={null}
      closable={false}
      loading={props?.isFetchingPortalData || props?.isFetchingBillDetails}
      onCancel={props?.closeCreateResubmissionModal}
      footer={
        <div className="resubmission-compare-footer">
          {/* <Text type="secondary" className="resubmission-compare-footer-hint">
            {hasHospitalBills
              ? "Compare mapped procedures with the Bill Audit hospital bill before submit."
              : "Review procedures and fill resubmission reason before submit."}
          </Text> */}
          <div className="resubmission-compare-footer-actions">
            <Button onClick={props?.closeCreateResubmissionModal}>Cancel</Button>
            <Button
              type="primary"
              loading={props?.isSubmitting}
              onClick={props?.handleSubmit}
              className="resubmission-compare-submit"
            >
              Submit
            </Button>
          </div>
        </div>
      }
    >
      <div className="resubmission-compare-shell">
        <div className="resubmission-compare-header">
          <div className="resubmission-compare-header-text">
            <h2 className="resubmission-compare-title">Resubmission Case</h2>
            {/* <p className="resubmission-compare-subtitle">
              {hasHospitalBills
                ? "Map and verify procedures against the hospital bill from Bill Audit."
                : "Add or update procedures and capture the resubmission reason."}
            </p> */}
          </div>

          <div className="resubmission-compare-header-controls">
            {hasHospitalBills && (
              <Segmented
                value={layoutMode}
                onChange={(value) => setLayoutMode(value as LayoutMode)}
                options={[
                  {
                    label: (
                      <Tooltip title="Compare procedures and bill">
                        <span className="resubmission-compare-seg-item">
                          <ColumnWidthOutlined />
                          Split
                        </span>
                      </Tooltip>
                    ),
                    value: "split",
                  },
                  {
                    label: (
                      <Tooltip title="Procedures only">
                        <span className="resubmission-compare-seg-item">
                          <FormOutlined />
                          Procedures
                        </span>
                      </Tooltip>
                    ),
                    value: "procedures",
                  },
                  {
                    label: (
                      <Tooltip title="Hospital bill only">
                        <span className="resubmission-compare-seg-item">
                          <FileTextOutlined />
                          Bill
                        </span>
                      </Tooltip>
                    ),
                    value: "bill",
                  },
                ]}
              />
            )}
            <Button
              type="text"
              className="resubmission-compare-close"
              onClick={props?.closeCreateResubmissionModal}
              aria-label="Close"
              icon={<CloseOutlined />}
            />
          </div>
        </div>

        <div
          className={`resubmission-compare-body ${
            isSplit ? "is-split" : "is-single"
          }`}
        >
          {showProcedures && (
            <section
              className={`resubmission-compare-panel procedures-panel ${
                isSplit ? "panel-60" : "panel-full"
              }`}
            >
              <div className="resubmission-compare-panel-head">
                <div>
                  <span className="resubmission-compare-panel-title">
                    Procedures
                  </span>
                  {/* <span className="resubmission-compare-panel-meta">
                    Edit mapped items and amounts
                  </span> */}
                </div>
              </div>

              <div className="resubmission-compare-panel-scroll">
                <ResubmissionProcedureCghsNew
                  resubmissionProcedure={props?.resubmissionProcedure}
                  formattedAccreditationOptions={
                    props?.formattedAccreditationOptions
                  }
                  formattedTreatingDoctorsOptions={
                    props?.formattedTreatingDoctorsOptions
                  }
                  formattedProcedureReferral={props?.formattedProcedureReferral}
                  formattedProceduresOptions={props?.formattedProceduresOptions}
                  handleGetProcedureAmount={props?.handleGetProcedureAmount}
                  handleGetProcedureStrats={props?.handleGetProcedureStrats}
                  patientDetails={props?.patientDetails}
                  procedureStrats={props?.procedureStrats}
                  handleDeleteProcedure={props?.handleDeleteProcedure}
                  isModalOpen={props?.isModalOpen}
                  setIsModalOpen={props?.setIsModalOpen}
                  handleOk={props?.handleOk}
                  handleCancel={props?.handleCancel}
                  form={props?.form}
                  resubmissionReasonOptions={props?.resubmissionReasonOptions}
                  isFetchingBillDetails={props?.isFetchingBillDetails}
                  procedureForm={props?.procedureForm}
                />
              </div>

              <div className="resubmission-compare-reason-bar">
                <ReasonOfResubmissionCghsNew
                  reasonOfResubmissionForm={props?.reasonOfResubmissionForm}
                  resubmissionReasonOptions={props?.resubmissionReasonOptions}
                />
              </div>
            </section>
          )}

          {showBill && (
            <section
              className={`resubmission-compare-panel bill-panel ${
                isSplit ? "panel-40" : "panel-full"
              }`}
            >
              <div className="resubmission-compare-panel-head">
                <div>
                  <span className="resubmission-compare-panel-title">
                    Hospital Bill
                  </span>
                  {/* <span className="resubmission-compare-panel-meta">
                    From Bill Audit
                    {hospitalBills.length > 1
                      ? ` · ${hospitalBills.length} files`
                      : ""}
                  </span> */}
                </div>
              </div>

              {hospitalBills?.length > 1 && (
                <Tabs
                  size="small"
                  className="resubmission-compare-bill-tabs"
                  activeKey={String(selectedBillIdx)}
                  onChange={(key) => setSelectedBillIdx(Number(key))}
                  items={hospitalBills?.map((bill, idx) => ({
                    key: String(idx),
                    label: bill?.fileName
                      ? `Bill ${idx + 1}`
                      : `Bill ${idx + 1}`,
                    title: bill?.fileName,
                  }))}
                />
              )}

              <div className="resubmission-compare-bill-preview">
                {selectedBill?.fileUrl ? (
                  <iframe
                    title={selectedBill?.fileName || "Hospital Bill"}
                    src={`${selectedBill.fileUrl.split("#")[0]}#zoom=page-width`}
                    className="resubmission-compare-bill-iframe"
                  />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No hospital bill found in Bill Audit"
                  />
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ResubmissionCompareModalCghsNew;
